import React, { useCallback, useRef, useState } from "react";
import { FaFolder } from "../../utils/icons";
import { collectFiles, readEntries } from "../../utils/folderFiles";

/**
 * Batch entry (left half of the split): drop or pick a FOLDER of many studies.
 * Presentational — normalizes the selection into File[] and hands it up; the
 * grouping (parseFolder) lives in the logic lane (useBatchUpload). Flat house
 * style, matching DropzonePanel.
 */
interface FolderDropzoneProps {
  onFiles: (files: File[]) => void;
}

export const FolderDropzone: React.FC<FolderDropzoneProps> = ({ onFiles }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const openPicker = useCallback(() => inputRef.current?.click(), []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const dt = e.dataTransfer;
      const entries = readEntries(dt);
      const flat = Array.from(dt.files ?? []);
      void (async () => {
        const walked = entries.length ? await collectFiles(entries) : [];
        const files = walked.length ? walked : flat;
        if (files.length > 0) onFiles(files);
      })();
    },
    [onFiles],
  );

  return (
    <div
      data-testid="batch-folder-dropzone"
      role="button"
      tabIndex={0}
      aria-label="Drag and drop a folder of DICOM studies, or click to browse"
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={[
        "group flex h-full cursor-pointer flex-col items-center justify-center rounded border border-dashed px-7 py-16 text-center transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
        dragging
          ? "border-signal bg-signal-tint"
          : "border-border-strong bg-signal-ambient hover:border-signal-stroke-soft",
      ].join(" ")}
    >
      <div
        className={[
          "mb-4 grid h-11 w-11 place-items-center rounded border transition-colors",
          dragging
            ? "border-signal text-signal"
            : "border-border-strong text-signal group-hover:border-signal-stroke-soft",
        ].join(" ")}
      >
        <FaFolder className="h-5 w-5" />
      </div>
      <p className="text-[15px] font-medium text-fg">
        {dragging ? (
          "Release to load studies"
        ) : (
          <>
            Drop a folder of studies, or <span className="font-semibold text-signal">browse</span>
          </>
        )}
      </p>
      <p className="mt-1.5 text-[13px] text-fg/55">
        A folder containing <span className="font-semibold">many studies</span> — each is grouped
        and uploaded
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-6 font-mono text-[10px] tracking-[0.06em] text-fg/25">
        <span>CT ONLY</span>
        <span>MANY STUDIES</span>
        <span>UPLOADED IN PARALLEL</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        data-testid="batch-folder-input"
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
      />
    </div>
  );
};
